import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Paper,
  Toolbar,
  Divider,
  Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { LoadingButton } from '@mui/lab';
import { ArrowBack } from '@mui/icons-material';
import { useApi } from '@/providers/ApiContext';
import {
  createDocument,
  getDocument,
  updateDocument,
} from '@/actions/document';
import DocumentNameField from './DocumentNameField';
import DocumentIdField from './DocumentIdField';
import FileUploadField from './FileUploadField';
import { useUser } from '@/providers/UserContext';
import { useDocTypes } from '@/providers/DocTypesContext';
import LoadingMask from '../../../shared/LoadingMask';
import ErrorSnackBar from '../../../shared/ErrorSnackBar';
import type { Document, DocumentType } from '@/types';

interface FileContents {
  title: string;
  mime_type: string;
  base64_contents: string;
}

function AdminDocumentForm(): React.JSX.Element {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const api = useApi();
  const user = useUser();
  const doc_types = useDocTypes();

  const [document, setDocument] = useState<Document | null>(null);
  const [document_type, setDocumentType] = useState<DocumentType | undefined>();
  const [loading_document, setLoadingDocument] = useState<boolean>(true);
  const [revision, setRevision] = useState<number>(0);
  const [pristine, setPristine] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>(document?.name || '');
  const [name_error, setNameError] = useState<string | boolean>(false);
  const [doc_id, setDocId] = useState<string>(document?.doc_id || '');
  const [doc_id_error, setDocIdError] = useState<string | boolean>(false);
  const [contents, setContents] = useState<FileContents | string>('');
  const [contents_error, setContentsError] = useState<string | boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);

  const type = sp.get('type');
  const document_id = sp.get('document_id');

  useEffect(() => {
    if (doc_types && !document_type) {
      if (document_id) {
        if (document) {
          const doc_type = doc_types.find(
            (doc_type) => doc_type.entity_id === document.document_type_id,
          );
          setDocumentType(doc_type);
        }
      } else {
        const doc_type = doc_types.find((doc_type) => doc_type.code === type);
        setDocumentType(doc_type);
      }
    }
  }, [document_type, document_id, document, type, doc_types]);

  useEffect(() => {
    if (user?.entity_id && !document && api) {
      if (document_id) {
        getDocument(api, document_id)
          .then((data) => {
            setDocument(data);
            setName(data?.name || '');
            setDocId(data?.doc_id || '');
            setLoadingDocument(false);
          })
          .catch((error) => {
            console.log(error);
            navigate('/error', {
              state: {
                errorMessage: 'Failed to load document for editing',
                errorDetails:
                  (error as Error).message ||
                  'Unable to retrieve the document for editing. This could be due to network issues, server problems, or the document may no longer exist.',
                returnPath: `/admin/documents/edit?document_id=${document_id}`,
              },
            });
          });
      } else {
        setLoadingDocument(false);
      }
    }
  }, [api, user?.entity_id, document, document_id, navigate]);

  useEffect(() => {
    setRevision((prevState) => prevState + 1);
  }, [document, doc_types, document_type]);

  const handleSubmit = useCallback(
    async (e?: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e?.preventDefault();

      if (!api) return;
      if (!document_id && !document_type) {
        // Cannot create document without document_type
        return;
      }

      if (document_id) {
        // Update case
        const payload: {
          name: string;
          doc_id: string;
          document_type_id?: string;
          contents?: {
            title: string;
            type: string;
            base64_contents: string;
          };
        } = {
          name,
          doc_id,
        };
        console.log(payload);
        try {
          setLoading(true);
          await updateDocument(api, document_id, payload);
          navigate(-1); // Go back to the list
        } catch (err) {
          console.error('Error updating document:', err);
          const error = err as Error;
          setApiError(
            error.message ||
              'Unable to update the document. This could be due to network issues, server problems, or invalid data.',
          );
          setLoading(false);
        }
      } else {
        // Create case - document_type_id is required
        if (!document_type) {
          return;
        }
        const payload: {
          name: string;
          doc_id: string;
          document_type_id: string;
          contents?: {
            title: string;
            type: string;
            base64_contents: string;
          };
        } = {
          name,
          doc_id,
          document_type_id: document_type.entity_id,
        };
        if (
          typeof contents === 'object' &&
          contents !== null &&
          'title' in contents
        ) {
          payload.contents = {
            title: contents.title,
            type: contents.mime_type,
            base64_contents: contents.base64_contents,
          };
        }
        console.log(payload);
        try {
          setLoading(true);
          await createDocument(api, payload);
          navigate(-1); // Go back to the list
        } catch (err) {
          console.error('Error creating document:', err);
          const error = err as Error;
          setApiError(
            error.message ||
              'Unable to create the document. This could be due to network issues, server problems, or invalid data.',
          );
          setLoading(false);
        }
      }
    },
    [api, document_type, name, document_id, doc_id, contents, navigate],
  );

  if (loading_document) {
    return <LoadingMask />;
  }

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
          <Toolbar
            sx={{
              minHeight: '55px !important',
            }}
          >
            <Stack spacing={2} direction='row' alignItems='center'>
              <IconButton onClick={() => navigate(-1)} disabled={loading}>
                <ArrowBack />
              </IconButton>
              <Typography variant='h5'>
                {document
                  ? `Edit ${document.name}`
                  : `Load New ${document_type?.name} Document`}
              </Typography>
            </Stack>
          </Toolbar>
        </Paper>
      </Grid>
      <Grid size={12}>
        <Box
          padding='15px'
          sx={{
            maxWidth: 600,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <DocumentNameField
                key={`name_${revision}`}
                value={name}
                error={name_error}
                setValue={setName}
                setError={setNameError}
                setChanged={() => setPristine(false)}
              />
            </Grid>
            <Grid size={12}>
              <DocumentIdField
                key={`doc_id_${revision}`}
                value={doc_id}
                error={doc_id_error}
                setValue={setDocId}
                setError={setDocIdError}
                setChanged={() => setPristine(false)}
              />
            </Grid>
            {!document_id ? (
              // no good way to managed file "versions" yet
              <Grid size={12}>
                <FileUploadField
                  key={`contents_${revision}`}
                  value={contents}
                  error={contents_error}
                  setValue={(value) =>
                    setContents(value as FileContents | string)
                  }
                  setError={setContentsError}
                  setChanged={() => setPristine(false)}
                />
              </Grid>
            ) : undefined}
          </Grid>
        </Box>
      </Grid>
      <Grid size={12}>
        <Divider />
        <Toolbar>
          <Stack spacing={2} direction='row'>
            <LoadingButton
              variant='contained'
              onClick={handleSubmit}
              loading={loading}
              disabled={
                pristine ||
                Boolean(name_error) ||
                Boolean(doc_id_error) ||
                Boolean(contents_error) ||
                !Boolean(name) ||
                !Boolean(doc_id) ||
                (!document_id && !Boolean(contents))
              }
            >
              Submit
            </LoadingButton>

            <Button
              variant='outlined'
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </Button>
          </Stack>
        </Toolbar>
      </Grid>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Grid>
  );
}

export default AdminDocumentForm;
